import { datadogRum } from '@datadog/browser-rum';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { PrimaryButton, SecondaryButton } from '@shared/components/buttons';
import { useMemo, useRef, useState } from 'react';
import type {
  ProcessingResult,
  ProductOrderMatch,
  ReceivingSession,
} from '../../../data/receiving';
import { partitionUnknownProducts } from '../../../data/receiving';
import { logUnexpectedApiError } from '../../../helpers/api-errors';
import { pluralize } from '../../../helpers/pluralize';
import type { ReceivingService } from '../../../services/receiving';
import { DuplicateInvoices } from './duplicate-invoices';
import { DuplicateItemCodesTable } from './duplicate-item-codes-table';
import { InvalidRowsTable } from './invalid-rows-table';
import { InvoiceTotals } from './invoice-totals-summary';
import { MatchedProductsTable } from './matched-products-table';
import { ParseErrorsList } from './parse-errors-list';
import { SpeculativeMatchesTable } from './speculative-matches-table';
import { StepHeading } from './step-heading';
import { UnknownProductsTable } from './unknown-products-table';

export type ReviewResultsProps = {
  storeId: string;
  sessionId: string;
  session: ReceivingSession;
  result: ProcessingResult;
  readOnly: boolean;
  service: ReceivingService;
  // Owning-vendor id → name, for the vendors a multi-vendor session spans.
  // Undefined or a single entry means a single-vendor session — provenance
  // (the Vendor column, the duplicate-item-code suffix) stays hidden.
  vendorNames?: Map<string, string>;
};

// Backend discriminant for matches produced by the suggested-item-code
// heuristic. Rows with this value go to the Speculative table and get the
// amber highlight on their Item code cell.
const SPECULATIVE_MATCH_METHOD = 'suggested_item_code';

// Stable fallbacks so downstream components don't see a fresh reference on
// every parent render when the backend field is null/undefined.
const EMPTY_UNKNOWN: NonNullable<ProcessingResult['unknown_products']> = [];
const EMPTY_INVALID_ROWS: NonNullable<ProcessingResult['not_parsed_rows']> = [];
const EMPTY_PARSE_ERRORS: NonNullable<ProcessingResult['parse_errors']> = {};
const EMPTY_DUPLICATES: ReceivingSession['duplicates'] = [];

type Counts = {
  matched: number;
  speculative: number;
  unknown: number;
  invalidRows: number;
  parseErrors: number;
};

function splitMatches(result: ProcessingResult): {
  speculative: ProductOrderMatch[];
  fullyMatched: ProductOrderMatch[];
} {
  const speculative: ProductOrderMatch[] = [];
  const fullyMatched: ProductOrderMatch[] = [];
  for (const m of result.matched ?? []) {
    (m.matched_by === SPECULATIVE_MATCH_METHOD ? speculative : fullyMatched).push(m);
  }

  return { speculative, fullyMatched };
}

function deriveCounts(result: ProcessingResult, speculative: ProductOrderMatch[]): Counts {
  const total = (result.matched ?? []).length;

  return {
    matched: total - speculative.length,
    speculative: speculative.length,
    unknown: (result.unknown_products ?? []).length,
    invalidRows: (result.not_parsed_rows ?? []).length,
    parseErrors: Object.keys(result.parse_errors ?? {}).length,
  };
}

type SectionTone = 'warn' | 'error' | 'ok';

const SECTION_TONES: Record<
  SectionTone,
  { header: string; title: string; desc: string; pill: string }
> = {
  error: {
    header: 'bg-rose-50',
    title: 'text-rose-900',
    desc: 'text-rose-800',
    pill: 'bg-rose-600 text-white',
  },
  warn: {
    header: 'bg-amber-50',
    title: 'text-amber-900',
    desc: 'text-amber-800',
    pill: 'bg-amber-600 text-white',
  },
  ok: {
    header: 'bg-emerald-50',
    title: 'text-emerald-900',
    desc: 'text-emerald-800',
    pill: 'bg-emerald-700 text-white',
  },
};

function CountPill({
  count,
  tone,
  fixedWidth = false,
}: {
  count: number;
  tone: SectionTone;
  fixedWidth?: boolean;
}) {
  const sizing = fixedWidth ? 'w-10' : 'min-w-[1.5rem] px-2';

  return (
    <span
      className={`inline-flex items-center justify-center h-6 rounded-full text-sm font-semibold tabular-nums ${sizing} ${SECTION_TONES[tone].pill}`}
    >
      {count}
    </span>
  );
}

const CHIP_BG: Record<SectionTone, string> = {
  error: 'bg-rose-50 text-rose-900',
  warn: 'bg-amber-50 text-amber-900',
  ok: 'bg-emerald-50 text-emerald-900',
};

function SummaryChips({ counts, actions }: { counts: Counts; actions?: React.ReactNode }) {
  const chips: Array<{ count: number; tone: SectionTone; label: string; visible: boolean }> = [
    {
      count: counts.parseErrors,
      tone: 'error',
      label: pluralize(counts.parseErrors, 'parse error', 'parse errors'),
      visible: counts.parseErrors > 0,
    },
    {
      count: counts.invalidRows,
      tone: 'error',
      label: pluralize(counts.invalidRows, 'invalid row', 'invalid rows'),
      visible: counts.invalidRows > 0,
    },
    { count: counts.unknown, tone: 'warn', label: 'unknown', visible: counts.unknown > 0 },
    {
      count: counts.speculative,
      tone: 'warn',
      label: 'speculative',
      visible: counts.speculative > 0,
    },
    { count: counts.matched, tone: 'ok', label: 'matched', visible: true },
  ];

  return (
    <div data-testid="summary-chips" className="flex flex-wrap items-center gap-2 px-4 py-3">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 mr-1">
        Summary
      </span>
      {chips
        .filter((c) => c.visible)
        .map((c) => (
          <span
            key={c.label}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${CHIP_BG[c.tone]}`}
          >
            <CountPill count={c.count} tone={c.tone} />
            {c.label}
          </span>
        ))}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

type ReviewSectionProps = {
  tone: SectionTone;
  title: string;
  count: number;
  description: string;
  defaultOpen: boolean;
  onFirstOpen?: () => void;
  children?: React.ReactNode;
};

export function ReviewSection({
  tone,
  title,
  count,
  description,
  defaultOpen,
  onFirstOpen,
  children,
}: ReviewSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const firedRef = useRef(false);
  const tones = SECTION_TONES[tone];

  return (
    <details
      open={open}
      className="bg-white"
      onToggle={(e) => {
        const nextOpen = (e.currentTarget as HTMLDetailsElement).open;
        setOpen(nextOpen);
        if (nextOpen && !firedRef.current) {
          firedRef.current = true;
          onFirstOpen?.();
        }
      }}
    >
      <summary
        className={`cursor-pointer list-none [&::-webkit-details-marker]:hidden flex flex-wrap items-center gap-x-3 gap-y-1 mx-4 px-3 py-2.5 rounded-md ${tones.header}`}
      >
        <ChevronRightIcon
          className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''} ${tones.title}`}
        />
        <CountPill count={count} tone={tone} fixedWidth />
        <h3 className={`text-base font-semibold ${tones.title}`}>{title}</h3>
        <span className={`text-sm font-normal ${tones.desc}`}>{description}</span>
      </summary>
      <div className="py-3">{children}</div>
    </details>
  );
}

export function ReviewResults({
  storeId,
  sessionId,
  session,
  result,
  readOnly,
  service,
  vendorNames,
}: ReviewResultsProps) {
  const { speculative, fullyMatched } = useMemo(() => splitMatches(result), [result]);
  const counts = useMemo(() => deriveCounts(result, speculative), [result, speculative]);
  const unknown = result.unknown_products ?? EMPTY_UNKNOWN;
  const { duplicateItemCodes, noMatch } = partitionUnknownProducts(unknown);
  const invalidRows = result.not_parsed_rows ?? EMPTY_INVALID_ROWS;
  const parseErrors = result.parse_errors ?? EMPTY_PARSE_ERRORS;

  const [submitting, setSubmitting] = useState(false);
  const [showConfirmWarning, setShowConfirmWarning] = useState(false);
  // ORD-451. The backend refuses Confirm with a 409 while the session has
  // flagged duplicates unless the request says to receive them anyway; the
  // checkbox is what stops the owner meeting that as an error rather than as a
  // question. Local state on purpose: a refresh just re-asks, which is fine
  // for a best-effort guard.
  const duplicates = session.duplicates ?? EMPTY_DUPLICATES;
  const [receiveDuplicates, setReceiveDuplicates] = useState(false);
  const canSend =
    !readOnly &&
    !submitting &&
    result.receiving_file != null &&
    (duplicates.length === 0 || receiveDuplicates);

  const handleSendClick = () => {
    if (!canSend) {
      return;
    }

    // Spec §10: "The Confirm button warns that, once pressed, the receiving is
    // sent to LiquorPOS and can't be cancelled — even if this window is closed."
    setShowConfirmWarning(true);
  };

  const handleSendConfirmed = async () => {
    setShowConfirmWarning(false);
    setSubmitting(true);
    try {
      await service.confirm(storeId, sessionId, { receiveDuplicates });
    } catch (err) {
      logUnexpectedApiError('receiving: confirm failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <StepHeading className="px-4">Review conversion results</StepHeading>
      {/* One band: the breakdown and the action first, then the reconciliation
          the owner checks against the paper invoice in hand. */}
      <div className="bg-white border-b border-gray-200">
        <SummaryChips
          counts={counts}
          actions={
            <PrimaryButton onClick={handleSendClick} disabled={!canSend}>
              Send to LiquorPOS
            </PrimaryButton>
          }
        />
        <InvoiceTotals result={result} />
        <DuplicateInvoices
          duplicates={duplicates}
          acknowledged={receiveDuplicates}
          onAcknowledgedChange={setReceiveDuplicates}
          readOnly={readOnly}
        />
      </div>
      {showConfirmWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          data-testid="confirm-warning-dialog"
        >
          <div className="bg-white rounded-md shadow-xl max-w-sm w-full p-6 flex flex-col gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Send to LiquorPOS?</h3>
            <p className="text-sm text-gray-600">
              Once sent, this receiving is on its way to LiquorPOS and{' '}
              <strong>cannot be cancelled</strong>, even if you close this window. If something goes
              wrong it can still be <strong>Abandoned</strong>, which clears the file and ends it,
              but it may already have reached LiquorPOS.
            </p>
            <div className="flex justify-end gap-3">
              <SecondaryButton onClick={() => setShowConfirmWarning(false)}>
                Go back
              </SecondaryButton>
              <PrimaryButton onClick={handleSendConfirmed} data-testid="confirm-send-btn">
                Send to LiquorPOS
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
      {result.receiving_file == null && (
        <p className="mx-4 mt-3 px-3 py-2 text-sm font-medium text-rose-900 bg-rose-50 rounded-md">
          No import file was produced. Cancel and retry.
        </p>
      )}

      <div className="flex flex-col gap-3 pt-3">
        {counts.parseErrors > 0 && (
          <ReviewSection
            tone="error"
            title="Parse errors"
            count={counts.parseErrors}
            description="Files that couldn't be opened. Re-upload or check the format."
            defaultOpen
          >
            <ParseErrorsList errors={parseErrors} />
          </ReviewSection>
        )}

        {invalidRows.length > 0 && (
          <ReviewSection
            tone="error"
            title="Invalid rows"
            count={invalidRows.length}
            description="Rows we couldn't parse. The vendor may have changed their invoice format."
            defaultOpen
          >
            <InvalidRowsTable rows={invalidRows} />
          </ReviewSection>
        )}

        {duplicateItemCodes.length > 0 && (
          <ReviewSection
            tone="warn"
            title="Duplicate item codes"
            count={duplicateItemCodes.length}
            description="Two products in LiquorPOS share the same item code, so we can't tell which one to receive. Often one of them is an older duplicate you've disabled. Give one a different item code in LiquorPOS, run an upload from your Companion, then process again."
            defaultOpen
          >
            <DuplicateItemCodesTable rows={duplicateItemCodes} vendorNames={vendorNames} />
          </ReviewSection>
        )}

        {noMatch.length > 0 && (
          <ReviewSection
            tone="warn"
            title="Unknown products"
            count={noMatch.length}
            description="Parsed but not in LiquorPOS. Likely a missing or wrong item code."
            defaultOpen
          >
            <UnknownProductsTable rows={noMatch} />
          </ReviewSection>
        )}

        {speculative.length > 0 && (
          <ReviewSection
            tone="warn"
            title="Speculative matches"
            count={speculative.length}
            description="Matched by heuristic, not exact item code. Verify before confirming."
            defaultOpen
          >
            <SpeculativeMatchesTable rows={speculative} vendorNames={vendorNames} />
          </ReviewSection>
        )}

        <ReviewSection
          tone="ok"
          title="Matched products"
          count={fullyMatched.length}
          description="Exact item-code matches. Expand to sanity-check."
          defaultOpen={false}
          onFirstOpen={() =>
            datadogRum.addAction('receiving_matched_expanded', { session_id: sessionId })
          }
        >
          <MatchedProductsTable rows={fullyMatched} vendorNames={vendorNames} />
        </ReviewSection>
      </div>
    </div>
  );
}
